import { useMemo, useState } from 'react';
import { useRecordContext, useGetList } from 'react-admin';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Stack,
  Typography,
  Divider,
} from '@mui/material';
import { Funeral } from '../../types';
import { AircallButton } from './FuneralShowHelpers';

interface Phone {
  label?: string;
  number?: string;
  ext?: string | null;
  note?: string;
}

interface Place {
  id: number;
  name: string;
  type?: string;
  region?: string | null;
  address?: string | null;
  city?: string | null;
  state?: string | null;
  postal_code?: string | null;
  country?: string | null;
  phone_number?: string | null;
  phones?: Phone[] | null;
  abbreviation?: string | null;
}

const PHONE_LABELS: Record<string, string> = {
  general: 'General',
  admitting: 'Admitting',
  records: 'Records',
  pathology: 'Pathology',
  after_hours: 'After hours',
  palliative: 'Palliative care',
  nursing_coordinator: 'Nursing coordinator',
  direct: 'Direct',
  security: 'Security',
  general_alt: 'General (alt.)',
  records_alt: 'Records (alt.)',
};

const labelText = (label?: string) =>
  label ? PHONE_LABELS[label] || label.replace(/_/g, ' ').replace(/^\w/, (c) => c.toUpperCase()) : 'Phone';

// Aircall's URL scheme dials a bare number; normalize NANP numbers to E.164 so
// "(514) 252-6000" launches correctly.
const toDialable = (num?: string | null): string => {
  const d = (num || '').replace(/\D/g, '');
  if (d.length === 10) return `+1${d}`;
  if (d.length === 11 && d.startsWith('1')) return `+${d}`;
  return num || '';
};

const PhoneRow = ({ p }: { p: Phone }) => {
  if (!p?.number) return null;
  return (
    <Box display="flex" alignItems="center" justifyContent="space-between" gap={1}>
      <Box minWidth={0}>
        <Typography variant="caption" color="text.secondary" display="block" lineHeight={1.2}>
          {labelText(p.label)}
        </Typography>
        <Typography variant="body2">
          {p.number}
          {p.ext ? ` · ext. ${p.ext}` : ''}
          {p.note ? ` (${p.note})` : ''}
        </Typography>
      </Box>
      <AircallButton phoneNumber={toDialable(p.number)} />
    </Box>
  );
};

// Facility place-of-death types. For these we show only the facility NAME —
// we already have the facility's address/phones on file in `places`. Residence
// and "other" have no facility to look up, so we show the full structured
// address instead.
const FACILITY_TYPES = new Set(['hospital', 'chsld', 'palliative']);

// Format the structured place_of_death_address jsonb into a single line.
// Legacy rows may hold a plain string, which we pass through.
export const formatPlaceOfDeathAddress = (addr: Funeral['place_of_death_address']): string => {
  if (!addr) return '';
  if (typeof addr === 'string') return addr.trim();
  return [addr.adresse, addr.municipalite, addr.province, addr.codePostal, addr.pays]
    .map((s) => (s == null ? '' : String(s).trim()))
    .filter(Boolean)
    .join(', ');
};

// True when there's anything to render for this funeral's place of death — used
// to gate the sidebar row so residence-only (structured-address) cases show.
export const hasPlaceOfDeath = (record?: Funeral): boolean =>
  !!(record?.place_of_death || formatPlaceOfDeathAddress(record?.place_of_death_address));

// Renders the funeral's place of death.
//   • Facility types (hospital/CHSLD/palliative): show the facility NAME. When
//     it matches a `places` row the name becomes clickable, opening a modal with
//     the facility's address and phone numbers/extensions (each with a
//     click-to-call Aircall button).
//   • Residence / other: show the full formatted structured address.
//
// `funeral` must be passed when this renders inside a <ReferenceField> (e.g. the
// sidebar's Deceased card), where the record context is the client, not the case.
export const PlaceOfDeathValue = ({ funeral }: { funeral?: Funeral }) => {
  const contextRecord = useRecordContext<Funeral>();
  const record = funeral || contextRecord;
  const [open, setOpen] = useState(false);

  const placeName = record?.place_of_death || '';
  const podType = (record?.place_of_death_type || '').toLowerCase();
  const isFacilityType = FACILITY_TYPES.has(podType);
  const region = (record?.region || '').toLowerCase();
  const structuredAddress = formatPlaceOfDeathAddress(record?.place_of_death_address);

  const { data } = useGetList<Place>(
    'places',
    { filter: { name: placeName, active: true }, pagination: { page: 1, perPage: 10 } },
    { enabled: isFacilityType && !!placeName },
  );

  const facility = useMemo(() => {
    if (!data?.length) return null;
    // Prefer a region match when the case region is known.
    return data.find((p) => (region ? String(p.region || '').toLowerCase() === region : true)) || data[0];
  }, [data, region]);

  // Residence / other (or any non-facility type): show the full structured
  // address; fall back to the free-text label if we have no structured address.
  if (!isFacilityType) {
    const text = structuredAddress || placeName;
    return text ? <span>{text}</span> : null;
  }

  // Facility: show the name (fall back to the address if somehow unnamed).
  if (!placeName) return structuredAddress ? <span>{structuredAddress}</span> : null;
  if (!facility) return <span>{placeName}</span>;

  const addressLine = [facility.address, facility.city, facility.state, facility.postal_code]
    .filter(Boolean)
    .join(', ');
  const phones = (facility.phones || []).filter((p) => p?.number);

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="text-left text-[13px] text-foreground underline decoration-dotted underline-offset-2 hover:text-primary cursor-pointer bg-transparent p-0 border-0"
        title="View facility contact details"
      >
        {placeName}
      </button>

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ pb: 0.5 }}>
          {facility.name}
          {facility.abbreviation ? (
            <Typography component="span" color="text.secondary" sx={{ ml: 1 }}>
              ({facility.abbreviation})
            </Typography>
          ) : null}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={1.5} sx={{ mt: 1 }}>
            {addressLine && (
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Address
                </Typography>
                <Typography variant="body2">{addressLine}</Typography>
              </Box>
            )}

            {(phones.length > 0 || facility.phone_number) && (
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
                  Phone
                </Typography>
                <Stack spacing={1} divider={<Divider flexItem />}>
                  {phones.length > 0 ? (
                    phones.map((p, i) => <PhoneRow key={i} p={p} />)
                  ) : (
                    <PhoneRow p={{ label: 'general', number: facility.phone_number || undefined }} />
                  )}
                </Stack>
              </Box>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
